import { getApiBaseUrl } from '@/config/api';

const API_BASE_URL = `${getApiBaseUrl()}/manager-shooting`;

interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
}

async function fetchWithCredentials<T = any>(url: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 20000);
  const abortFromCaller = () => controller.abort();
  options.signal?.addEventListener('abort', abortFromCaller, { once: true });

  let response: Response;
  try {
    response = await fetch(url, {
      ...options,
      signal: controller.signal,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  } catch (error) {
    if (controller.signal.aborted && !options.signal?.aborted) {
      throw new Error('Le chargement du planning a expiré. Veuillez réessayer.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    options.signal?.removeEventListener('abort', abortFromCaller);
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      typeof errorBody?.message === 'string'
        ? errorBody.message
        : Array.isArray(errorBody?.message)
          ? errorBody.message[0]
          : `HTTP error: ${response.status}`;
    throw new Error(message);
  }

  return response.json();
}

export class ManagerShootingService {
  static async validateUpgr(idShooting: number): Promise<{ success: boolean }> {
    return fetchWithCredentials(`${API_BASE_URL}/validate-upgr`, {
      method: 'POST', body: JSON.stringify({ idShooting }),
    });
  }

  static async publishDrafts(idShooting: number, preview = false): Promise<{
    success: boolean; count: number; missingWeight: number;
  }> {
    return fetchWithCredentials(`${API_BASE_URL}/${preview ? 'publish-drafts-preview' : 'publish-drafts'}`, {
      method: 'POST', body: JSON.stringify({ idShooting }),
    });
  }

  static async getPlanningListe(params: {
    idShootingPlanningStatut?: number[];
    grossiste?: string;
    dateDemandeDebut?: string;
    dateDemandeFin?: string;
    dateShootingDebut?: string;
    dateShootingFin?: string;
    idVendeur?: number;
    page?: number;
  }): Promise<ApiResponse> {
    return fetchWithCredentials<ApiResponse>(`${API_BASE_URL}/liste`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  static async deletePlanning(params: {
    idShooting: number;
  }): Promise<ApiResponse> {
    return fetchWithCredentials<ApiResponse>(`${API_BASE_URL}/delete`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  static async saveMel(params: {
    idShooting: number;
    mel: number;
  }): Promise<ApiResponse> {
    return fetchWithCredentials<ApiResponse>(`${API_BASE_URL}/save-mel`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  static async savePrestation(params: {
    idShooting: number;
    prestation: number;
    previousPrestation: number;
  }): Promise<ApiResponse> {
    return fetchWithCredentials<ApiResponse>(`${API_BASE_URL}/save-prestation`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  static async savePlanning(params: {
    idShooting: number;
    dateShooting: string;
    sendEmail: boolean;
    vendeurEmail1: string;
    vendeurEmail2: string;
  }): Promise<ApiResponse> {
    return fetchWithCredentials<ApiResponse>(`${API_BASE_URL}/save-planning`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  static async cancelPlanning(params: {
    idShooting: number;
  }): Promise<ApiResponse> {
    return fetchWithCredentials<ApiResponse>(`${API_BASE_URL}/cancel-planning`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }
}
